import gql from 'graphql-tag';

import {
  type EntitySettings,
  type EntitySettingsInput,
} from '../generated/graphql';
import {type Graph, type View} from '../types/graphql';
import {ENTITY_RATE_LIMITS_FRAGMENT} from './admin';

export interface UserContextResult {
  viewer: {
    views: Graph<Pick<View, 'id' | 'spec'>>;
  };
}

export const USER_CONTEXT_QUERY = gql`
  query UserContext {
    viewer {
      id
      views(viewType: "userContext") {
        edges {
          node {
            id
            spec
          }
        }
      }
    }
  }
`;

export const VIEWER_USERINFO_QUERY = gql`
  query ViewerUserInfo {
    viewer {
      id
      userInfo
    }
  }
`;

export const USER_ORGANIZATIONS_QUERY = gql`
  query UserOrganizations($emailDomain: String) {
    viewer {
      id
      organizations: organizations {
        id
        name
        orgType
        members {
          id
          admin
        }
        teams {
          id
          name
        }
        subscriptions {
          id
          subscriptionType
          privileges
          plan {
            id
            name
            planType
            displayName
          }
        }
      }
      openMatchingOrganizations: organizations(emailDomain: $emailDomain) {
        id
        name
        orgType
        flags
        members {
          orgID
          id
          admin
        }
        subscriptions {
          id
          status
        }
        teams {
          id
          name
          memberCount
          settings {
            openToMatchingOrgEmailDomainUsers
          }
        }
      }
    }
  }
`;

export const ENTITY_STORAGE_BYTES_QUERY = gql`
  query EntityStorageBytes($name: String!) {
    entity(name: $name) {
      id
      storageBytes
    }
  }
`;

export const ENTITY_QUERY = gql`
  query Entity($name: String!) {
    entity(name: $name) {
      id
      name
      available
      photoUrl
      readOnly
      readOnlyAdmin
      isTeam
      privateOnly
      codeSavingEnabled
      artifactTTLDurationSeconds
      settings {
        hidden
        openToMatchingOrgEmailDomainUsers
        membersCanInvite
        disableMagicLinkSharing
        defaultCloudRegion {
          id
          provider
          region
        }
        storageBucketInfo {
          provider
          name
          kmsKeyID
          awsExternalID
          path
        }
        disableMembersEditArtifactTTL
      }
      defaultAccess
      isPaid
      ...EntityRateLimits
      members {
        id
        admin
        role
        pending
        email
        username
        name
        photoUrl
        accountType
        apiKey
      }
      defaultAlerts {
        id
        condition {
          __typename
          ... on FinishedRunCondition {
            success
          }
          ... on StoppedRunCondition {
            minimumRunDuration
          }
        }
        subscriptions {
          __typename
          ... on EmailSubscription {
            id
          }
          ... on SlackChannelSubscription {
            id
          }
        }
      }
      integrations {
        edges {
          node {
            id
            __typename
            ... on SlackIntegration {
              teamName
              channelName
            }
            ... on GenericWebhookIntegration {
              id
              urlEndpoint
              name
              accessTokenRef
              secretRef
              createdAt
            }
          }
        }
      }
      computeHours
      organization {
        id
        name
        orgType
        billingUser {
          id
          username
        }
        subscriptions {
          id
          plan {
            id
            name
            planType
          }
          privileges
          seats
          expiresAt
          subscriptionType
          status
        }
        members {
          orgID
          id
          username
          name
          photoUrl
          admin
          role
        }
        pendingInvites {
          id
          email
        }
      }
      invites {
        edges {
          node {
            id
            createdAt
            name
            toUser {
              id
              email
              username
            }
          }
        }
      }
    }
    user(userName: $name) {
      id
      username
      accountType
    }
  }
  ${ENTITY_RATE_LIMITS_FRAGMENT}
`;

export const ENTITY_INTEGRATIONS_QUERY = gql`
  query EntityIntegrations($name: String!) {
    entity(name: $name) {
      id
      name
      integrations {
        edges {
          node {
            id
            __typename
            ... on SlackIntegration {
              teamName
              channelName
            }
            ... on GenericWebhookIntegration {
              id
              urlEndpoint
              name
              accessTokenRef
              secretRef
              createdAt
            }
          }
        }
      }
    }
  }
`;

export const USER_ROLE_FRAGMENT = gql`
  fragment UserRoleFragment on User {
    accountType
    admin
    deletedAt
    email
    username
  }
`;

// Lightweight email fragment for queries that don't need expensive Auth0 fields
export const EMAIL_BASIC_FRAGMENT = gql`
  fragment EmailBasic on Email {
    id
    emailAddress
    createdAt
    type
  }
`;

export const SEARCH_USERS_QUERY = gql`
  query SearchUsers(
    $query: String
    $queryEmail: String
    $ids: [ID!]
    $usernames: [String!]
  ) {
    users(
      query: $query
      queryEmail: $queryEmail
      ids: $ids
      usernames: $usernames
    ) {
      edges {
        node {
          emails {
            ...EmailBasic
          }
          id
          name
          loggedInAt
          userInfo
          ...UserRoleFragment
        }
      }
    }
  }
  ${USER_ROLE_FRAGMENT}
  ${EMAIL_BASIC_FRAGMENT}
`;

// Powers a UI for selecting a user
const USER_FRAGMENT = gql`
  fragment UserFragment on User {
    name
    email
    username
    deletedAt
    photoUrl
  }
`;
export const FIND_USERS_QUERY = gql`
  query FindUsers($query: String!, $limit: Int!) {
    users(query: $query, first: $limit) {
      edges {
        node {
          id
          ...UserFragment
        }
      }
    }
  }
  ${USER_FRAGMENT}
`;
export const FIND_USERS_BY_USERNAME_QUERY = gql`
  query FindUsersByUsername($usernames: [String!]!, $limit: Int!) {
    users(usernames: $usernames, first: $limit) {
      edges {
        node {
          id
          ...UserFragment
        }
      }
    }
  }
  ${USER_FRAGMENT}
`;

export const UPDATE_USER_ROLE = gql`
  mutation UpdateUserRole($id: ID!, $role: String!) {
    updateUserRole(input: { id: $id, accountType: $role }) {
      user {
        id
        name
        username
        email
        admin
        deletedAt
        accountType
      }
    }
  }
`;

export const UPDATE_USER_ROLE_V2 = gql`
  mutation UpdateUserRoleV2($id: ID!, $role: String!) {
    updateUserRoleV2(input: { id: $id, accountType: $role }) {
      user {
        id
        name
        username
        email
        admin
        deletedAt
        accountType
      }
    }
  }
`;

export const SET_USER_ADMIN = gql`
  mutation SetUserAdmin($id: ID!, $admin: Boolean!) {
    updateUser(input: { id: $id, admin: $admin }) {
      user {
        id
        name
        username
        email
        admin
        deletedAt
      }
    }
  }
`;
export const PURGE_USER = gql`
  mutation PurgeUser($username: String!, $email: String!) {
    purgeUser(input: { username: $username, email: $email }) {
      user {
        id
        deletedAt
      }
    }
  }
`;

export const DELETE_USER = gql`
  mutation DeleteUser($id: ID!) {
    deleteUser(input: { id: $id }) {
      user {
        id
        name
        username
        email
        admin
        deletedAt
      }
    }
  }
`;
export const UNDELETE_USER = gql`
  mutation UndeleteUser($id: ID!) {
    undeleteUser(input: { id: $id }) {
      user {
        id
        name
        username
        email
        admin
        deletedAt
      }
    }
  }
`;

export const CREATE_INVITE = gql`
  mutation CreateInvite(
    $entityName: String!
    $email: String
    $username: String
    $admin: Boolean
    $role: String
    $modelsSeat: String
    $weaveRole: String
  ) {
    createInvite(
      input: {
        entityName: $entityName
        email: $email
        username: $username
        admin: $admin
        role: $role
        modelsSeat: $modelsSeat
        weaveRole: $weaveRole
      }
    ) {
      invite {
        id
        name
        email
        createdAt
        toUser {
          id
          name
        }
      }
      success
      remainingSeats
      remainingViewOnlySeats
    }
  }
`;

export const DELETE_INVITE = gql`
  mutation DeleteInvite($id: String, $entityName: String) {
    deleteInvite(input: { id: $id, entityName: $entityName }) {
      success
    }
  }
`;

export interface DeleteInviteMutationData {
  deleteInvite: {
    success: boolean;
  };
}

export interface DeleteInviteMutationVariables {
  id: string;
  entityName: string;
}

export const UPDATE_MEMBER = gql`
  mutation UpdateMember($entityName: String!, $user: ID!, $role: String!) {
    updateMember(input: { entityName: $entityName, user: $user, role: $role }) {
      member {
        id
        role
      }
    }
  }
`;

export interface UpdateMemberMutationData {
  updateMember: {
    member: {
      id: string;
      role: string;
    };
  };
}

export interface UpdateMemberMutationVariables {
  entityName: string;
  user: string;
  role: string;
}

export const CREATE_SERVICE_ACCOUNT = gql`
  mutation CreateServiceAccount(
    $entityName: String!
    $description: String!
    $organizationId: ID
  ) {
    createServiceAccount(
      input: {
        description: $description
        entityName: $entityName
        organizationId: $organizationId
      }
    ) {
      user {
        id
        name
        apiKeys {
          edges {
            node {
              id
              name
            }
          }
        }
      }
    }
  }
`;

export const CREATE_ORG_SERVICE_ACCOUNT = gql`
  mutation CreateOrgServiceAccount(
    $description: String!
    $defaultEntityId: ID!
    $organizationId: ID!
  ) {
    createOrgServiceAccount(
      input: {
        description: $description
        defaultEntityId: $defaultEntityId
        organizationId: $organizationId
      }
    ) {
      user {
        id
        name
        apiKeys {
          edges {
            node {
              id
              name
            }
          }
        }
      }
    }
  }
`;

export const DELETE_SERVICE_ACCOUNT = gql`
  mutation DeleteServiceAccount($id: ID!) {
    deleteServiceAccount(input: { id: $id }) {
      user {
        id
      }
    }
  }
`;

// entity field must contain all fields that other viewer > teams queries
// on the page (like SEARCH_NAV_QUERY) have.
// Otherwise, the newly created entity won't have enough
// information to update the Apollo cache,
// and Apollo will silently give up and wipe the queries
// that want fields that the new entity lacks.
// Very confusing.
export const CREATE_ENTITY = gql`
  mutation CreateEntity($name: String!, $invited: String, $framework: String) {
    createEntity(
      input: { name: $name, defaultFramework: $framework, invited: $invited }
    ) {
      entity {
        id
        name
        photoUrl
        invitedTeam
        projects(first: 100) {
          edges {
            node {
              id
              name
              entityName
            }
          }
        }
      }
      apiKey {
        id
        name
      }
    }
  }
`;

export const CREATE_USER_FROM_ADMIN_MUTATION = gql`
  mutation CreateUserFromAdmin(
    $email: String!
    $admin: Boolean
    $accountType: String
    $organizationID: ID
    $modelsSeat: String
    $weaveRole: String
  ) {
    createUser(
      input: {
        email: $email
        admin: $admin
        accountType: $accountType
        organizationID: $organizationID
        modelsSeat: $modelsSeat
        weaveRole: $weaveRole
      }
    ) {
      user {
        id
        name
        username
        email
        admin
      }
    }
  }
`;

export const CREATE_USER_MUTATION = gql`
  mutation CreateEntityOnboardingFlow($name: String!) {
    createEntity(input: { name: $name }) {
      entity {
        id
      }
    }
  }
`;

export const CHECK_AVAILABILITY_QUERY = gql`
  query CheckAvailability($name: String) {
    entity(name: $name) {
      id
      available
    }
  }
`;

export const UPDATE_USER_MUTATION = gql`
  mutation UpdateUser(
    $defaultEntity: String
    $defaultFramework: String
    $photoUrl: String
    $code: String
    $settingsVisited: Boolean
    $galleryVisited: Boolean
    $onboardingHidden: Boolean
    $hideTeamsFromPublic: Boolean
  ) {
    updateUser(
      input: {
        defaultEntity: $defaultEntity
        defaultFramework: $defaultFramework
        photoUrl: $photoUrl
        code: $code
        settingsVisited: $settingsVisited
        galleryVisited: $galleryVisited
        onboardingHidden: $onboardingHidden
        hideTeamsFromPublic: $hideTeamsFromPublic
      }
    ) {
      user {
        id
        flags
        entity
        hideTeamsFromPublic
        teams {
          edges {
            node {
              id
              name
              isTeam
            }
          }
        }
      }
    }
  }
`;

export const UPDATE_USER_INFO_MUTATION = gql`
  mutation UpdateUserInfo(
    $id: ID
    $name: String
    $photoUrl: String
    $userInfo: JSONString
    $defaultEntity: String
    $primaryEmail: String
    $password: String
  ) {
    updateUser(
      input: {
        id: $id
        name: $name
        userInfo: $userInfo
        defaultEntity: $defaultEntity
        photoUrl: $photoUrl
        primaryEmail: $primaryEmail
        password: $password
      }
    ) {
      user {
        id
        name
        userInfo
        photoUrl
        entity
        teams {
          edges {
            node {
              id
              name
              isTeam
            }
          }
        }
      }
    }
  }
`;

export const UPDATE_USER_EMAIL_MUTATION = gql`
  mutation UpdateUserEmail($id: ID!, $type: EmailType) {
    updateUserEmail(input: { id: $id, type: $type }) {
      success
      email {
        id
        emailAddress
      }
    }
  }
`;

export const DELETE_USER_EMAIL_MUTATION = gql`
  mutation DeleteUserEmail($id: ID!) {
    deleteUserEmail(input: { id: $id }) {
      success
    }
  }
`;

export const GENERATE_API_KEY_MUTATION = gql`
  mutation GenerateApiKey($description: String) {
    generateApiKey(input: { description: $description }) {
      apiKey {
        id
        name
        description
        apiKeyId
        createdAt
        expiresAt
        user {
          id
          name
        }
      }
      secretApiKey
    }
  }
`;

export interface GenerateApiKeyMutationData {
  generateApiKey: {
    apiKey: {
      id: string;
      name: string;
      description?: string;
      apiKeyId?: string;
      createdAt?: string;
      expiresAt?: string;
      user?: {
        id: string;
        name?: string;
      };
    };
    secretApiKey?: string;
  };
}

export interface GenerateApiKeyMutationVariables {
  description: string;
}

export const DELETE_API_KEY_MUTATION = gql`
  mutation DeleteApiKey($id: String!) {
    deleteApiKey(input: { id: $id }) {
      success
    }
  }
`;

export interface DeleteApiKeyMutationData {
  deleteApiKey: {
    success: boolean;
  };
}

export interface DeleteApiKeyMutationVariables {
  id: string;
}

export const CREATE_SERVICE_ACCOUNT_API_KEY_MUTATION = gql`
  mutation CreateServiceAccountApiKey(
    $serviceAccountId: ID!
    $description: String
  ) {
    createServiceAccountApiKey(
      input: { serviceAccountId: $serviceAccountId, description: $description }
    ) {
      apiKey {
        id
        name
        description
        apiKeyId
        createdAt
        expiresAt
        user {
          id
          name
        }
      }
      secretApiKey
      success
    }
  }
`;

export interface CreateServiceAccountApiKeyMutationData {
  createServiceAccountApiKey: {
    apiKey: {
      id: string;
      name: string;
      description: string;
      apiKeyId: string;
      createdAt: string;
      expiresAt: string;
      user: {
        id: string;
        name: string;
      };
    };
    secretApiKey: string;
    success: boolean;
  };
}

export const DELETE_SERVICE_ACCOUNT_API_KEY_MUTATION = gql`
  mutation DeleteServiceAccountApiKey(
    $apiKeyId: String!
    $serviceAccountId: ID!
  ) {
    deleteServiceAccountApiKey(
      input: {
        apiKeyId: $apiKeyId
        serviceAccountId: $serviceAccountId
      }
    ) {
      success
    }
  }
`;

export interface DeleteServiceAccountApiKeyMutationData {
  deleteServiceAccountApiKey: {
    success: boolean;
  };
}

export interface DeleteServiceAccountApiKeyMutationVariables {
  apiKeyId: string;
  serviceAccountId: string;
}

export const UPDATE_SERVICE_ACCOUNT_API_KEY_MUTATION = gql`
  mutation UpdateServiceAccountApiKey(
    $apiKeyId: String!
    $serviceAccountId: ID!
    $description: String!
  ) {
    updateServiceAccountApiKey(
      input: {
        apiKeyId: $apiKeyId
        serviceAccountId: $serviceAccountId
        description: $description
      }
    ) {
      success
      apiKey {
        id
        description
      }
    }
  }
`;

export interface UpdateServiceAccountApiKeyMutationData {
  updateServiceAccountApiKey: {
    success: boolean;
    apiKey: {
      id: string;
      description: string;
    };
  };
}

export interface UpdateServiceAccountApiKeyMutationVariables {
  apiKeyId: string;
  serviceAccountId: string;
  description: string;
}

export const UPDATE_API_KEY_MUTATION = gql`
  mutation UpdateApiKey($apiKeyId: String!, $description: String!) {
    updateApiKey(input: {id: $apiKeyId, description: $description}) {
      success
      apiKey {
        id
        description
      }
    }
  }
`;

export interface UpdateApiKeyMutationData {
  updateApiKey: {
    success: boolean;
    apiKey: {
      id: string;
      description: string;
    };
  };
}

export interface UpdateApiKeyMutationVariables {
  apiKeyId: string;
  description: string;
}

export const ORGANIZATION_TEAMS_WITH_VIEWER_ROLE = gql`
  query OrganizationTeamsWithViewerRole($orgName: String!) {
    organization(name: $orgName) {
      id
      name
      teams {
        id
        name
        members {
          id
          username
          role
          memberRole {
            ID
            name
            permissions {
              name
              groupName
              displayName
            }
          }
        }
      }
    }
    viewer {
      id
      username
    }
  }
`;

export interface OrganizationTeamsWithViewerRoleData {
  organization: {
    id: string;
    name: string;
    teams: Array<{
      id: string;
      name: string;
      members: Array<{
        id: string;
        username: string;
        role: string;
        memberRole: {
          ID: string;
          name: string;
          permissions: Array<{
            name: string;
            groupName: string;
            displayName: string;
          }>;
        } | null;
      }>;
    }>;
  } | null;
  viewer: {
    id: string;
    username: string;
  } | null;
}

/**
 * Helper function to filter teams where the viewer is an admin
 * @param data The query result from ORGANIZATION_TEAMS_WITH_VIEWER_ROLE
 * @returns Array of teams where the viewer has admin role
 */
export function getViewerAdminTeams(data: any): Array<{
  id: string;
  name: string;
}> {
  if (!data?.organization?.teams || !data?.viewer) {
    return [];
  }

  return data.organization.teams
    .filter((team: any) => {
      const viewerMembership = team.members.find(
        (member: any) => member.username === data.viewer?.username
      );

      // Check if viewer is admin (role === 'admin' or memberRole.name === 'Admin')
      return (
        viewerMembership &&
        (viewerMembership.role === 'admin' ||
          viewerMembership.memberRole?.name === 'Admin')
      );
    })
    .map((team: any) => ({
      id: team.id,
      name: team.name,
    }));
}

export const UPDATE_ENTITY = gql`
  mutation UpdateEntity(
    $entityName: String!
    $defaultAccess: String
    $privateOnly: Boolean
    $codeSavingEnabled: Boolean
    $settings: EntitySettingsInput
  ) {
    updateEntity(
      input: {
        entity: $entityName
        defaultAccess: $defaultAccess
        privateOnly: $privateOnly
        codeSavingEnabled: $codeSavingEnabled
        settings: $settings
      }
    ) {
      entity {
        id
        defaultAccess
        privateOnly
        limits
        settings {
          hidden
          defaultCloudRegion {
            id
            provider
            region
          }
        }
      }
    }
  }
`;

export interface UpdateEntityMutationData {
  updateEntity: {
    entity: {
      id: string;
      defaultAccess: string;
      privateOnly: boolean;
      limits: string;
      settings: EntitySettings;
    };
  };
}

export interface UpdateEntityMutationVariables {
  entityName: string;
  defaultAccess?: string;
  privateOnly?: boolean;
  codeSavingEnabled?: boolean;
  settings?: EntitySettingsInput;
}

export const RESET_PASSWORD_MUTATION = gql`
  mutation ResetPassword($email: String!) {
    resetPassword(input: { email: $email }) {
      success
    }
  }
`;

export interface ResetPasswordData {
  success: boolean;
}

export interface ResetPasswordVars {
  email: string;
}

export const VIEWER_ALL_PROJECTS_WITH_PATTERN = gql`
  query ViewerAllProjectsWithPattern($pattern: String, $limit: Int = 500) {
    viewer {
      id
      entity
      projects(first: $limit, pattern: $pattern) {
        edges {
          node {
            id
            name
            entityName
          }
        }
      }
    }
  }
`;
